import React from 'react';
import { motion } from 'framer-motion';
import { BellIcon, TrendingUpIcon, HeadphonesIcon, ChevronRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
const features = [{
  icon: <BellIcon size={36} data-id="element-395" />,
  title: 'Alertes intelligentes',
  description: 'Recevez des notifications personnalisées par e-mail, SMS ou directement sur la plateforme pour ne manquer aucune opportunité pertinente.',
  color: 'bg-orange-50 text-orange-500',
  link: '/alertes-intelligentes'
}, {
  icon: <TrendingUpIcon size={36} data-id="element-396" />,
  title: 'Analyse et veille stratégique',
  description: 'Accédez à des tableaux de bord analytiques et des rapports détaillés pour identifier les tendances et prendre des décisions éclairées.',
  color: 'bg-blue-50 text-blue-500',
  link: '/veille-strategique'
}, {
  icon: <HeadphonesIcon size={36} data-id="element-397" />,
  title: 'Assistance Premium',
  description: "Bénéficiez d'un accompagnement personnalisé pour la préparation de vos dossiers et d'un coaching expert pour maximiser vos chances de succès.",
  color: 'bg-green-50 text-green-500',
  link: '/assistance-premium'
}];
export function FeaturesSection() {
  return <section id="features" className="py-24 bg-gray-50 relative overflow-hidden" data-id="element-398">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-radial from-yellow-100 to-transparent opacity-40" data-id="element-399"></div>
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-radial from-blue-50 to-transparent opacity-40" data-id="element-400"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-401">
        <div className="text-center mb-16" data-id="element-402">
          <span className="inline-block px-3 py-1 bg-blue-50 text-blue-900 text-sm font-semibold rounded-full mb-4" data-id="element-403">
            Fonctionnalités
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-404">
            Des outils conçus pour votre succès
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-405">
            Des outils puissants pour vous aider à identifier et saisir les
            meilleures opportunités d'affaires en Afrique
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12" data-id="element-406">
          {features.map((feature, index) => <motion.div key={feature.title} className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 h-full transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: index * 0.2
        }} viewport={{
          once: true
        }} data-id="element-407">
              <div className={`inline-flex items-center justify-center w-16 h-16 ${feature.color} rounded-lg mb-6`} data-id="element-408">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4" data-id="element-409">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-410">
                {feature.description}
              </p>
              <div className="mt-6 pt-6 border-t border-gray-100" data-id="element-411">
                <Link to={feature.link} className="text-blue-600 font-medium flex items-center hover:text-blue-800 transition-colors" data-id="element-412">
                  En savoir plus
                  <ChevronRightIcon size={20} className="ml-1" data-id="element-413" />
                </Link>
              </div>
            </motion.div>)}
        </div>
        {/* Additional feature highlight */}
        <motion.div className="mt-16 bg-blue-900 rounded-2xl p-8 md:p-12 text-white shadow-xl relative overflow-hidden" initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6
      }} viewport={{
        once: true
      }} data-id="element-414">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10" data-id="element-415">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-416">
              <defs data-id="element-417">
                <pattern id="smallGrid" width="20" height="20" patternUnits="userSpaceOnUse" data-id="element-418">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="white" strokeWidth="0.5" data-id="element-419" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#smallGrid)" data-id="element-420" />
            </svg>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center relative z-10" data-id="element-421">
            <div data-id="element-422">
              <span className="inline-block px-3 py-1 bg-blue-800 text-yellow-500 text-sm font-semibold rounded-full mb-4" data-id="element-423">
                Nouveau
              </span>
              <h3 className="text-2xl md:text-3xl font-bold mb-4" data-id="element-424">
                Intelligence artificielle au service de vos appels d'offres
              </h3>
              <p className="text-blue-100 mb-6" data-id="element-425">
                Notre système d'IA analyse en continu les appels d'offres pour
                vous recommander les opportunités qui correspondent le mieux à
                votre profil et à votre historique de succès.
              </p>
              <Link to="/veille-strategique" className="inline-flex items-center justify-center px-6 py-3 bg-yellow-500 text-blue-900 font-semibold rounded-lg hover:bg-yellow-400 transition-colors" data-id="element-426">
                Découvrir cette fonctionnalité
              </Link>
            </div>
            <div className="flex justify-center" data-id="element-427">
              <div className="w-64 h-64 relative" data-id="element-428">
                <div className="absolute inset-0 bg-blue-800 rounded-full opacity-30 animate-pulse" data-id="element-429"></div>
                <div className="absolute inset-4 bg-blue-700 rounded-full opacity-60" data-id="element-430"></div>
                <div className="absolute inset-8 bg-blue-600 rounded-full opacity-80" data-id="element-431"></div>
                <div className="absolute inset-12 bg-blue-500 rounded-full" data-id="element-432"></div>
                <div className="absolute inset-16 bg-white rounded-full flex items-center justify-center" data-id="element-433">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-blue-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-434">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" data-id="element-435" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}